// ─────────────────────────────────────────────────────────────────────────
// OnlineJobSheetPage.tsx
// Top-level page replacing online_job_sheet.html. Orchestrates the same
// state machine as the original inline script:
//   pending --(GPS gate, then start photo, then start)--> in_progress
//   in_progress --(delivery photo)--> [signature] --(save)--> completed
//
// Wrap this in <LanguageProvider> at the app root (or directly here if this
// page is mounted standalone via its own route).
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SteeringWheel,
  CalendarBlank,
  AirplaneTilt,
  PhoneCall,
  UserCircle,
  MapPinLine,
  Car,
  CheckCircle,
  PlayCircle,
  SealCheck,
  CalendarCheck,
  ClockClockwise,
  NavigationArrow,
  WarningCircle,
  Prohibit,
} from '@phosphor-icons/react';
import { LanguageProvider, useLanguage, formatDateTime } from './LanguageContext';
import { useJobSheetStorage } from './useJobSheetStorage';
import { uploadApi } from './api';
import { usePushNotifications } from './usePushNotifications';
import { BellRinging, Bell } from '@phosphor-icons/react';
import { useLiveLocationBroadcast, type LiveLocationPing } from './useLiveLocationBroadcast';
import AuditLogSection from './AuditLogSection';
import DeliveryPhotoModal from './DeliveryPhotoModal';
import StartJobPhotoModal from './StartJobPhotoModal';
import SignatureModal from './SignatureModal';
import InAppBrowserNotice from './InAppBrowserNotice';
import InstallPWAPrompt from './InstallPWAPrompt';
import type { JobSheetJob, ItineraryStop } from './jobSheet.types';

// Formats a real driver GPS fix into the "lat, lng" string format used
// throughout the app for auditStartGps / auditEndGps / deliveryPhotoGps.
function formatDriverGps(loc: { lat: number; lng: number }): string {
  return `${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}`;
}

// ── Itinerary parsing (ports renderItinerary's stop-splitting logic) ──
function parseItinerary(raw: string, labels: { pickup: string; final: string; mid: string }): ItineraryStop[] {
  if (!raw) return [];
  const stops = raw
    .split(/→|->/)
    .map((s) => s.trim())
    .filter(Boolean);
  return stops.map((stop, i) => ({
    label: stop,
    isFirst: i === 0,
    isLast: i === stops.length - 1,
  }));
}

function ItineraryTimeline({ raw }: { raw: string }) {
  const { t } = useLanguage();
  const stops = useMemo(
    () => parseItinerary(raw, { pickup: t('stopPickup'), final: t('stopFinal'), mid: t('stopMid') }),
    [raw, t],
  );

  if (stops.length === 0) return <p className="text-sm text-gray-400">—</p>;

  return (
    <div className="relative space-y-4 border-l-2 border-brand-200 pl-4">
      {stops.map((stop, i) => {
        const dotClass = stop.isFirst
          ? 'timeline-dot-start'
          : stop.isLast
            ? 'timeline-dot-end'
            : 'timeline-dot-mid';
        const stopLabel = stop.isFirst ? t('stopPickup') : stop.isLast ? t('stopFinal') : t('stopMid');
        return (
          <div className="relative" key={`${stop.label}-${i}`}>
            <span className={`absolute -left-[21px] top-1 h-3 w-3 rounded-full border-2 border-white ${dotClass}`} />
            <p className="text-sm font-bold text-gray-900">{stop.label}</p>
            <p className="text-[10px] text-gray-500">{stopLabel}</p>
          </div>
        );
      })}
    </div>
  );
}

function LocationGateSection({
  locationStatus,
}: {
  locationStatus: 'idle' | 'watching' | 'unsupported' | 'denied';
}) {
  const { t } = useLanguage();

  if (locationStatus === 'denied') {
    return (
      <section className="overflow-hidden rounded-2xl border-2 border-red-300 bg-white shadow-md">
        <div className="flex items-center gap-2 border-b border-red-200 bg-gradient-to-r from-red-50 to-red-100 px-4 py-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-200">
            <Prohibit weight="fill" className="text-base text-red-700" />
          </div>
          <h3 className="text-sm font-bold uppercase tracking-wide text-red-900">{t('locationDeniedTitle')}</h3>
        </div>
        <div className="p-4">
          <p className="text-xs leading-relaxed text-red-900">{t('locationDeniedBody')}</p>
        </div>
      </section>
    );
  }

  if (locationStatus === 'unsupported') {
    return (
      <section className="overflow-hidden rounded-2xl border-2 border-red-300 bg-white shadow-md">
        <div className="flex items-center gap-2 border-b border-red-200 bg-gradient-to-r from-red-50 to-red-100 px-4 py-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-200">
            <WarningCircle weight="fill" className="text-base text-red-700" />
          </div>
          <h3 className="text-sm font-bold uppercase tracking-wide text-red-900">{t('locationUnsupportedTitle')}</h3>
        </div>
        <div className="p-4">
          <p className="text-xs leading-relaxed text-red-900">{t('locationUnsupportedBody')}</p>
        </div>
      </section>
    );
  }

  // idle / watching (no fix yet) — waiting for the first real GPS fix
  return (
    <section className="overflow-hidden rounded-2xl border-2 border-amber-300 bg-white shadow-md">
      <div className="flex items-center gap-2 border-b border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100 px-4 py-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-200">
          <NavigationArrow weight="fill" className="animate-pulse text-base text-amber-700" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold uppercase tracking-wide text-amber-900">{t('locationRequiredTitle')}</h3>
          <p className="mt-0.5 text-[10px] text-amber-700">{t('locationRequiredSubtitle')}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 p-4">
        <span className="h-2.5 w-2.5 flex-shrink-0 animate-pulse rounded-full bg-amber-500" />
        <p className="text-xs font-semibold text-amber-800">{t('locationWaitingFix')}</p>
      </div>
    </section>
  );
}

function StatusBadge({ status }: { status: JobSheetJob['status'] }) {
  const { t } = useLanguage();
  if (status === 'completed') {
    return (
      <span className="flex items-center gap-1.5 rounded-full border border-green-200 bg-green-100 px-2.5 py-1 text-[10px] font-bold text-green-800 shadow-sm transition-all">
        <CheckCircle weight="fill" />
        {t('statusCompleted')}
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span className="flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-100 px-2.5 py-1 text-[10px] font-bold text-brand-900 shadow-sm transition-all">
        {t('statusInProgress')}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-900 shadow-sm transition-all">
      <span className="h-2 w-2 animate-pulse rounded-full bg-amber-600" />
      {t('statusReady')}
    </span>
  );
}

function InfoCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-brand-100 bg-white shadow-sm">
      <div className="info-card-header">
        {icon}
        <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-900">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function JobSheetContent() {
  const { t, lang, toggleLanguage, locale } = useLanguage();
  const { job, isLoaded, updateJob } = useJobSheetStorage();

  // ── Push notifications: แจ้งคนขับเมื่อแอดมิน approve สลิป/ยืนยันการชำระเงิน ──
  // ผูกกับ driver_token ตัวเดียวกับที่อยู่ใน URL (?token=...) ของลิงก์นี้เอง
  const driverTokenFromUrl = useMemo(
    () => new URLSearchParams(window.location.search).get('token'),
    [],
  );
  const { permission: pushPermission, status: pushStatus, subscribe: subscribePush } =
    usePushNotifications(driverTokenFromUrl);

  // ถ้าเคยกดอนุญาตแจ้งเตือนไว้แล้วในเบราว์เซอร์นี้ ให้ re-subscribe อัตโนมัติ
  // ทุกครั้งที่เปิดลิงก์ (เผื่อ subscription เก่าหมดอายุ) — ถ้ายังไม่เคยขอ
  // สิทธิ์เลยจะไม่ auto-prompt ต้องกดปุ่มกระดิ่งเอง
  useEffect(() => {
    if (!job || pushPermission !== 'granted') return;
    void subscribePush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, pushPermission]);

  const [isFetchingStartGps, setIsFetchingStartGps] = useState(false);
  const [locationNudge, setLocationNudge] = useState(false);
  const [startPhotoModalOpen, setStartPhotoModalOpen] = useState(false);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  // Holds the just-captured delivery photo between the two modals, before it's persisted.
  const [pendingDeliveryPhoto, setPendingDeliveryPhoto] = useState<string | null>(null);
  // Latest real GPS fix from this session's watch — a ref (not state) so we
  // always read the freshest value inside callbacks without re-creating them.
  const latestPingRef = useRef<LiveLocationPing | null>(null);

  const isPending = job?.status === 'pending';
  const isInProgress = job?.status === 'in_progress';
  const isCompleted = job?.status === 'completed';

  // Ask for location permission ONCE, right when the driver opens this link
  // (as soon as the job record loads) — then keep streaming this device's
  // REAL, live GPS position for as long as the page stays open, regardless
  // of job status. This is what the admin tracking map reads: the actual
  // position of the phone that opened this link, not a simulated one.
  const { status: locationStatus } = useLiveLocationBroadcast(Boolean(job), (ping) => {
    latestPingRef.current = ping;
    updateJob({ driverLocation: { lat: ping.lat, lng: ping.lng, updatedAt: ping.updatedAt, accuracy: ping.accuracy } });
  });

  // A real GPS fix must exist AND location must be actively on right now —
  // not merely "a fix was saved on this job at some point in the past".
  const hasRealLocation =
    locationStatus === 'watching' && (Boolean(job?.driverLocation) || latestPingRef.current !== null);
  const locationBlocked = isPending && !hasRealLocation;

  // ── Start job: now requires a live-captured photo (taken via
  //    StartJobPhotoModal) in addition to the GPS fix, so the driver's
  //    presence at the actual location is verified with both signals. ──
  const startJobWorkflow = useCallback(async (startPhotoDataURI: string) => {
    if (!job) return;
    // Real device position — guaranteed present because the button is
    // disabled by locationBlocked until a real, LIVE fix lands. Prefer the
    // freshest ping from this session's active watch over whatever was
    // last stored on the job record (which could be from an earlier visit).
    const realLoc = latestPingRef.current ?? job.driverLocation;
    if (!realLoc) return; // safety net; UI already prevents reaching here without it
    setIsFetchingStartGps(true);

    // อัปโหลดรูปยืนยันตำแหน่งขึ้นเซิร์ฟเวอร์จริงก่อน (เหมือน delivery/signature)
    // แล้วค่อยบันทึก "path ไฟล์" ที่ได้กลับมาลงในใบงาน — ถ้าอัปโหลดไม่สำเร็จ
    // (เช่นเน็ตหลุด) จะ fallback ไปเก็บ data URI ตรง ๆ แทน ไม่ให้หลักฐานหายไปเฉย ๆ
    let auditStartPhotoPath: string | undefined;
    try {
      const upload = await uploadApi.uploadDataUri(job.id, 'start', startPhotoDataURI, { token: driverTokenFromUrl });
      auditStartPhotoPath = upload.path;
    } catch {
      // fallback: ไม่สามารถอัปโหลดได้ — ปล่อยให้ auditStartPhotoPath เป็น undefined
    }

    const now = new Date();
    updateJob({
      status: 'in_progress',
      auditStartTime: formatDateTime(now, locale),
      auditStartGps: formatDriverGps(realLoc),
      auditStartAccuracy: typeof realLoc.accuracy === 'number' ? realLoc.accuracy : null,
      ...(auditStartPhotoPath ? { auditStartPhotoPath } : { auditStartPhotoURI: startPhotoDataURI }),
    });
    setIsFetchingStartGps(false);
    setStartPhotoModalOpen(false);
  }, [job, updateJob, locale]);

  const handleMainAction = useCallback(() => {
    if (!job) return;
    if (job.status === 'pending') {
      if (locationBlocked) {
        // Same nudge pattern as the payment-slip gate: flash + scroll the
        // GPS section into view instead of silently doing nothing.
        document
          .getElementById('location-gate-section')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setLocationNudge(true);
        window.setTimeout(() => setLocationNudge(false), 1600);
        return;
      }
      // GPS gate cleared — now require a live photo of the actual location
      // before actually flipping the job to in_progress.
      setStartPhotoModalOpen(true);
    } else if (job.status === 'in_progress') {
      setDeliveryModalOpen(true);
    }
  }, [job, locationBlocked]);

  const handleDeliveryPhotoConfirmed = useCallback((photoDataURI: string) => {
    setPendingDeliveryPhoto(photoDataURI);
    setDeliveryModalOpen(false);
    // Original used a 300ms pause for the bottom-sheet close transition before opening the next modal.
    window.setTimeout(() => setSignatureModalOpen(true), 300);
  }, []);

  const handleSignatureSaved = useCallback(
    async (signatureDataURI: string) => {
      if (!job) return;
      // Freshest real position available — this session's live tracker if
      // it's ticked since load, otherwise whatever's already on the record.
      const realLoc = latestPingRef.current ?? job.driverLocation ?? null;
      const now = new Date();

      // อัปโหลดรูปลายเซ็น + รูปส่งของขึ้นเซิร์ฟเวอร์จริงก่อน (แทนที่จะฝัง
      // base64 ยาว ๆ ลงฐานข้อมูลตรง ๆ) แล้วค่อยบันทึก "path ไฟล์" ที่ได้
      // กลับมาลงในใบงาน — ถ้าอัปโหลดไม่สำเร็จ (เช่นเน็ตหลุด) จะ fallback
      // ไปเก็บ data URI ตรง ๆ แทน เพื่อไม่ให้ข้อมูลของลูกค้าหายไปเฉย ๆ
      let signaturePath: string | undefined;
      let deliveryPhotoPath: string | undefined;
      try {
        const sigUpload = await uploadApi.uploadDataUri(job.id, 'signature', signatureDataURI, { token: driverTokenFromUrl });
        signaturePath = sigUpload.path;
      } catch {
        // fallback: ไม่สามารถอัปโหลดได้ — ปล่อยให้ signaturePath เป็น undefined
      }
      if (pendingDeliveryPhoto) {
        try {
          const photoUpload = await uploadApi.uploadDataUri(job.id, 'delivery', pendingDeliveryPhoto, { token: driverTokenFromUrl });
          deliveryPhotoPath = photoUpload.path;
        } catch {
          // fallback เช่นกัน
        }
      }

      updateJob({
        status: 'completed',
        auditEndTime: formatDateTime(now, locale),
        auditEndGps: realLoc ? formatDriverGps(realLoc) : null,
        auditEndAccuracy: realLoc && typeof realLoc.accuracy === 'number' ? realLoc.accuracy : null,
        // ถ้าอัปโหลดสำเร็จ ฝั่งเซิร์ฟเวอร์จะแปลง path -> URL เต็มให้อัตโนมัติ
        // ในทุก response ถัดไป (ดู job_row_to_json ใน helpers.php) ส่วนกรณี
        // fallback (อัปโหลดไม่สำเร็จ) ก็ยังส่ง data URI ตรง ๆ ไปตามช่องทางเดิม
        ...(signaturePath ? { signaturePath } : { signatureDataURI }),
        ...(deliveryPhotoPath
          ? { deliveryPhotoPath }
          : pendingDeliveryPhoto
            ? { deliveryPhotoURI: pendingDeliveryPhoto }
            : {}),
        deliveryPhotoTime: formatDateTime(now, locale),
        deliveryPhotoGps: realLoc ? formatDriverGps(realLoc) : null,
      });
      setSignatureModalOpen(false);
      setPendingDeliveryPhoto(null);
      window.setTimeout(() => {
        document.getElementById('success-screen-section')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 300);
    },
    [job, updateJob, locale, pendingDeliveryPhoto],
  );

  if (!isLoaded) {
    return (
      <>
        <InAppBrowserNotice />
        <div className="min-h-screen bg-brand-50" />
      </>
    );
  }

  if (!job) {
    return (
      <>
        <InAppBrowserNotice />
        <div className="flex min-h-screen items-center justify-center bg-brand-50 p-6 text-center text-gray-500">
          Job not found.
        </div>
      </>
    );
  }

  return (
    <div className="relative mx-auto min-h-screen max-w-md border-x border-gray-100 bg-white pb-12 shadow-2xl">
      <InAppBrowserNotice />

      {/* ═══ HEADER ═══ */}
      <header className="header-gradient sticky top-0 z-10 text-white shadow-xl">
        <div className="flex items-center justify-between px-5 pb-3 pt-4">
          <div className="flex items-center gap-3">
            <img
              src="/assets/richer-services-logo.jpg"
              alt="Richer Services"
              className="logo-ring h-11 w-11 flex-shrink-0 rounded-full object-cover"
            />
            <div>
              <h1 className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#DEC4F8]">
                Richer Services
              </h1>
              <h2 className="text-xl font-bold">{t('pageTitle')}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pushPermission !== 'unsupported' && (
              <button
                type="button"
                onClick={() => void subscribePush()}
                title={pushPermission === 'granted' ? 'เปิดการแจ้งเตือนอยู่' : 'เปิดการแจ้งเตือนเมื่อแอดมินยืนยันสลิป'}
                className={`flex h-9 w-9 items-center justify-center rounded-full border text-xs font-bold shadow-sm transition active:scale-95 ${
                  pushPermission === 'granted'
                    ? 'border-green-400 bg-green-600/80 hover:bg-green-600'
                    : 'border-brand-600 bg-brand-800 hover:bg-brand-600'
                }`}
              >
                {pushPermission === 'granted' ? <BellRinging weight="fill" /> : <Bell weight="bold" />}
              </button>
            )}
            <button
              type="button"
              onClick={toggleLanguage}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-600 bg-brand-800 text-xs font-bold shadow-sm transition hover:bg-brand-600 active:scale-95"
            >
              {lang === 'th' ? 'EN' : 'TH'}
            </button>
          </div>
        </div>

        <div className="gold-line" />

        <div className="driver-strip flex items-center gap-4 px-5 py-3">
          <div className="relative flex h-12 w-12 items-center justify-center rounded-full border border-brand-200 bg-white text-2xl text-brand-900 shadow-inner">
            <SteeringWheel weight="fill" />
            <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-green-400" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-medium uppercase tracking-wider text-brand-200">
              {t('assignedChauffeur')}
            </p>
            <p className="text-lg font-bold tracking-wide text-white">{job.driverName || '—'}</p>
          </div>
        </div>

        <div className="flex items-center justify-between border-b border-gray-100 bg-white px-5 py-2.5 text-brand-900 shadow-sm">
          <span className="text-sm font-medium">
            {t('jobIdLabel')} <span className="font-bold">{job.id}</span>
          </span>
          <StatusBadge status={job.status} />
        </div>
      </header>

      <main className="space-y-4 p-4">
        {/* ── Customer ── */}
        <InfoCard icon={<UserCircle className="text-xl text-brand-800" />} title={t('customerInfoTitle')}>
          <p className="mb-1 text-[10px] text-gray-500">{t('customerName')}</p>
          <p className="text-lg font-bold text-gray-900">{job.customerName || '—'}</p>
        </InfoCard>

        {/* ── Travel ── */}
        <InfoCard icon={<MapPinLine className="text-xl text-brand-800" />} title={t('travelInfoTitle')}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-[10px] text-gray-500">{t('bookingDate')}</p>
                <p className="text-sm font-medium text-gray-900">{job.bookingDate || '—'}</p>
              </div>
              <div>
                <p className="mb-1 text-[10px] text-gray-500">{t('serviceDate')}</p>
                <p className="flex items-center gap-1 text-sm font-bold text-brand-900">
                  <CalendarBlank weight="fill" className="text-brand-600" />
                  {job.serviceDate || '—'}
                </p>
              </div>
              <div>
                <p className="mb-1 text-[10px] text-gray-500">{t('startTime')}</p>
                <p className="text-lg font-bold text-gray-900">{job.startTime || '—'}</p>
              </div>
              <div>
                <p className="mb-1 text-[10px] text-gray-500">{t('endTime')}</p>
                <p className="text-lg font-bold text-gray-900">{job.endTime || '—'}</p>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4" />

            {job.flightNo && (
              <div>
                <p className="mb-1 text-[10px] text-gray-500">{t('flightNo')}</p>
                <p className="mb-4 flex items-center gap-1.5 text-sm font-bold text-gray-900">
                  <AirplaneTilt weight="fill" className="text-brand-600" />
                  <span>{job.flightNo}</span>
                </p>
              </div>
            )}

            <p className="mb-2 text-[10px] text-gray-500">{t('itinerary')}</p>
            <ItineraryTimeline raw={job.itinerary || ''} />
          </div>
        </InfoCard>

        {/* ── Vehicle & contact ── */}
        <InfoCard icon={<Car className="text-xl text-brand-800" />} title={t('vehicleInfoTitle')}>
          <div className="space-y-3">
            <div>
              <p className="mb-1 text-[10px] text-gray-500">{t('driverName')}</p>
              <p className="text-sm font-bold text-gray-900">{job.driverName || '—'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-[10px] text-gray-500">{t('driverPhone')}</p>
                <a
                  href={job.driverPhone ? `tel:${job.driverPhone.replace(/[^0-9+]/g, '')}` : '#'}
                  className="flex w-full items-center justify-center gap-1 rounded border border-brand-100 bg-brand-50 px-2 py-1.5 text-center text-sm font-bold text-brand-600 transition active:bg-brand-100"
                >
                  <PhoneCall weight="fill" />
                  <span>{job.driverPhone || '—'}</span>
                </a>
              </div>
              <div>
                <p className="mb-1 text-[10px] text-gray-500">{t('plate')}</p>
                <p className="plate-chip flex w-full items-center justify-center gap-1 px-2 py-1.5 text-center text-sm font-bold text-gray-900">
                  {job.plate || '—'}
                </p>
              </div>
            </div>
          </div>
        </InfoCard>

        {/* ── GPS gate (must have a real position before starting) ── */}
        {isPending && !hasRealLocation && (
          <div
            id="location-gate-section"
            className={
              locationNudge
                ? 'rounded-2xl ring-2 ring-amber-500 ring-offset-1 transition-shadow duration-300'
                : 'rounded-2xl ring-2 ring-transparent ring-offset-1 transition-shadow duration-300'
            }
          >
            <LocationGateSection locationStatus={locationStatus} />
          </div>
        )}

        {/* ── Audit log ──
            หมายเหตุ: ไม่ส่ง auditStartPhotoURI ให้ที่นี่โดยตั้งใจ — รูปยืนยัน
            ตำแหน่งตอนเริ่มงานที่คนขับถ่าย มีไว้ให้แอดมินตรวจสอบเท่านั้น
            (ดูใน JobDetailModal.tsx ฝั่ง Admin Dashboard) ไม่ต้องแสดงย้อนกลับ
            มาให้คนขับเห็นรูปตัวเองอีกในหน้านี้ */}
        <AuditLogSection
          auditStartTime={job.auditStartTime}
          auditStartGps={job.auditStartGps}
          auditStartAccuracy={job.auditStartAccuracy ?? null}
          auditEndTime={job.auditEndTime}
          auditEndGps={job.auditEndGps}
          auditEndAccuracy={job.auditEndAccuracy ?? null}
        />

        {/* ── Success screen ── */}
        {isCompleted && (
          <section
            id="success-screen-section"
            className="flex flex-col items-center space-y-4 rounded-2xl border-2 border-green-200 bg-gradient-to-b from-green-50/50 to-white p-6 text-center shadow-lg"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-green-200 bg-green-100 text-green-600 shadow-inner">
              <CheckCircle weight="fill" className="text-4xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold tracking-tight text-green-900">{t('jobCompletedTitle')}</h3>
              <p className="mt-1 text-xs text-gray-500">{t('jobCompletedSubtitle')}</p>
            </div>
            <div className="my-1 w-full border-t border-dashed border-gray-200" />
            <div className="w-full rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5 text-left">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {t('completionTimestamp')}
              </p>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm font-bold text-gray-800">
                <CalendarCheck className="text-base text-green-600" />
                <span>{job.auditEndTime || '—'}</span>
              </p>
            </div>
          </section>
        )}

        {/* ── Delivery photo display (after completion) ── */}
        {isCompleted && job.deliveryPhotoURI && (
          <section className="overflow-hidden rounded-xl border border-green-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-green-200 bg-green-50 px-4 py-3">
              <SealCheck weight="fill" className="text-xl text-green-700" />
              <h3 className="text-sm font-semibold uppercase tracking-wide text-green-900">
                {t('deliveryPhotoTitle')}
              </h3>
            </div>
            <div className="flex flex-col items-center justify-center bg-white p-4">
              <div className="w-full overflow-hidden rounded-xl border border-green-200 bg-white shadow-sm">
                <img src={job.deliveryPhotoURI} alt="Delivery" className="max-h-72 w-full object-contain" />
              </div>
              {job.deliveryPhotoTime && (
                <p className="mt-2 flex items-center gap-1 self-start text-[10px] text-green-700">
                  <ClockClockwise className="text-xs" />
                  {t('takenAt')}: {job.deliveryPhotoTime}
                </p>
              )}
            </div>
          </section>
        )}

        {/* ── Signature display (after completion) ── */}
        {isCompleted && job.signatureDataURI && (
          <section className="overflow-hidden rounded-xl border border-brand-100 bg-white shadow-sm">
            <div className="info-card-header">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-900">
                {t('signatureSectionTitle')}
              </h3>
            </div>
            <div className="flex flex-col items-center justify-center bg-white p-4">
              <div className="flex w-full justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-2">
                <img
                  id="signature-image"
                  src={job.signatureDataURI}
                  alt="Customer Signature"
                  className="max-h-32 object-contain mix-blend-multiply"
                />
              </div>
              <p className="mt-2 text-center text-[10px] text-gray-400">{t('signaturePermanentNote')}</p>
            </div>
          </section>
        )}
      </main>

      {/* ── Action bar ── */}
      <div className="action-bar-shadow fixed bottom-0 left-0 right-0 z-20 mx-auto max-w-md border-t border-brand-100 bg-white/96 p-4 backdrop-blur-md">
        {!isCompleted ? (
          <button
            type="button"
            onClick={handleMainAction}
            disabled={isFetchingStartGps || (isPending && locationBlocked)}
            className={[
              'btn-complete-glow flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-brand-900 py-4 text-lg font-bold text-white',
              isPending && locationBlocked ? 'opacity-40 cursor-not-allowed' : '',
            ].join(' ')}
          >
            {isInProgress ? (
              <CheckCircle weight="fill" className="text-2xl" />
            ) : isPending && locationBlocked ? (
              <NavigationArrow weight="fill" className="animate-pulse text-2xl" />
            ) : (
              <PlayCircle weight="fill" className="text-2xl" />
            )}
            <span>
              {isFetchingStartGps
                ? t('fetchingGps')
                : isPending && locationBlocked
                  ? t('waitingGpsButtonLabel')
                  : isInProgress
                    ? t('completeJob')
                    : t('startJob')}
            </span>
          </button>
        ) : (
          <div className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-green-500 text-lg font-bold text-white shadow-sm">
            <SealCheck weight="fill" className="text-2xl" />
            <span>{t('submittedSuccessfully')}</span>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <StartJobPhotoModal
        open={startPhotoModalOpen}
        onClose={() => setStartPhotoModalOpen(false)}
        onConfirm={(photo) => void startJobWorkflow(photo)}
        submitting={isFetchingStartGps}
      />
      <DeliveryPhotoModal
        open={deliveryModalOpen}
        onClose={() => setDeliveryModalOpen(false)}
        onConfirm={handleDeliveryPhotoConfirmed}
      />
      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSave={handleSignatureSaved}
      />

      {/* ── PWA install banner (Android native prompt / iOS manual instructions) ── */}
      <InstallPWAPrompt />
    </div>
  );
}

export default function OnlineJobSheetPage() {
  return (
    <LanguageProvider>
      <div className="min-h-screen bg-brand-50 pb-28 font-sans text-gray-800 antialiased">
        <JobSheetContent />
      </div>
    </LanguageProvider>
  );
}
